import type { RenderStat } from "./types";

/** Commit phases React passes to a Profiler `onRender` callback. */
type RenderPhase = "mount" | "update" | "nested-update";

const SLOW_RENDER_MS = 5;

/**
 * Counts React commits per Profiler `id`. Fed from a Profiler `onRender` callback (see
 * {@link createProfilerOnRender}), so the hot path is a single Map lookup plus integer increments —
 * no allocations per render. Disabled by default; {@link setEnabled} turns it on only when something
 * consumes the data (debug bridge or an active perf sink), keeping prod cost at zero otherwise.
 */
class RenderMetricsServiceImpl {
  private enabled = false;
  private readonly stats = new Map<string, RenderStat>();

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public record(id: string, phase: RenderPhase, durationMs: number): void {
    if (!this.enabled) return;

    const existing = this.stats.get(id);
    const stat: RenderStat = existing ?? {
      id,
      renders: 0,
      mounts: 0,
      updates: 0,
      slowRenders: 0,
      totalDurationMs: 0,
      maxDurationMs: 0,
      lastDurationMs: 0,
    };

    stat.renders += 1;
    if (phase === "mount") stat.mounts += 1;
    else stat.updates += 1;
    if (durationMs > SLOW_RENDER_MS) stat.slowRenders += 1;
    stat.totalDurationMs += durationMs;
    stat.lastDurationMs = durationMs;
    if (durationMs > stat.maxDurationMs) stat.maxDurationMs = durationMs;

    if (!existing) this.stats.set(id, stat);
  }

  public getStats(): RenderStat[] {
    return [...this.stats.values()].sort((a, b) => b.renders - a.renders);
  }

  public getTotals(): { count: number; slowRenders: number; maxDurationMs: number } {
    let count = 0;
    let slowRenders = 0;
    let maxDurationMs = 0;
    for (const stat of this.stats.values()) {
      count += stat.renders;
      slowRenders += stat.slowRenders;
      if (stat.maxDurationMs > maxDurationMs) maxDurationMs = stat.maxDurationMs;
    }
    return { count, slowRenders, maxDurationMs };
  }

  public reset(): void {
    this.stats.clear();
  }
}

export const RenderMetricsService = new RenderMetricsServiceImpl();
export { SLOW_RENDER_MS };
